import {
    JobAssistApi,
    GetBattlecardV3Params,
    LoanApplicationSubmitSource2,
} from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
    ApplicationDataStore,
    getApplicationDataDefaultValues,
} from '@servicetitan/financing-application-shared';

export const useCustomerData = ({
    jobId,
    tenantId,
    technicianId,
}: {
    jobId: string;
    tenantId: string;
    technicianId: string;
}) => {
    const { setApplicationData } = ApplicationDataStore.useStore();
    const [jobAssistApiClient] = useDependencies(JobAssistApi);

    // Query for battlecard data to get comprehensive job, customer, and location info
    const battlecardResult = useQuery({
        queryKey: ['getBattlecardV3', jobId, tenantId, technicianId],
        queryFn: () => {
            const params: GetBattlecardV3Params = {
                jobId: Number(jobId),
                limit: 1,
                threadpoolLimit: 3,
                pagesize: 5,
            };

            return jobAssistApiClient.getBattlecardV3(tenantId, technicianId, params);
        },
        enabled: !!jobId && !!tenantId && !!technicianId,
        throwOnError: false,
    });

    const battlecardData = battlecardResult.data?.data;

    useEffect(() => {
        if (battlecardData) {
            // Extract customer name from battlecard response
            const fullName = battlecardData.CustomerInfo?.Details?.CustomerName ?? '';
            const nameParts = fullName.trim().split(' ');
            const firstName = nameParts[0] ?? '';
            const lastName = nameParts.slice(1).join(' ') ?? '';

            // Get customer email directly from battlecard response
            const customerEmail = battlecardData.CustomerInfo?.Details?.CustomerEmail ?? '';
            const customerPhone = battlecardData.CustomerInfo?.Details?.CustomerPhoneNumber ?? '';

            // Get location data from LocationInfo.LocationForJob
            const jobLocation = battlecardData.LocationInfo?.LocationForJob;

            // Try to get structured address from customer locations matching the job location
            const structuredLocation = battlecardData.CustomerInfo?.Locations?.find(
                location => location.Id === jobLocation?.Id,
            );
            const locationAddress = structuredLocation?.Address;

            setApplicationData(
                getApplicationDataDefaultValues({
                    customerFirstName: firstName,
                    customerLastName: lastName,
                    customerEmail,
                    customerPhone,
                    serviceLocationStreet: locationAddress?.Street ?? '',
                    serviceLocationUnit: locationAddress?.Unit ?? '',
                    serviceLocationCity: locationAddress?.City ?? '',
                    serviceLocationState: locationAddress?.State ?? '',
                    serviceLocationZip: locationAddress?.Zip ?? '',
                    source: LoanApplicationSubmitSource2.Mobile,
                }),
            );
        }
    }, [battlecardData, setApplicationData]);

    return {
        battlecardResult,
        battlecardData,
        isLoading: battlecardResult.isLoading,
        error: battlecardResult.error,
    };
};
